import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext({ theme: "system", isDark: false, setTheme: () => {}, toggleTheme: () => {} });

export const ThemeProvider = ({ children }) => {
  // Default to system
  const getInitialTheme = () => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("theme") : null;
    return stored || "system";
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const [systemDark, setSystemDark] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const isDark = useMemo(() => {
    if (theme === "system") return systemDark;
    return theme === "dark";
  }, [theme, systemDark]);

  // Apply/remove .dark on <html>
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    window.localStorage.setItem("theme", theme);
  }, [theme, isDark]);

  useEffect(() => {
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.href = systemDark ? '/favicon-white.svg' : '/favicon.svg';
    }
  }, [systemDark]);

  // Listen to system changes and update systemDark in real time
  // When system theme changes, reset to "system" mode (clear manual override)
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event) => {
      setSystemDark(event.matches);
      // Reset to system mode when OS theme changes
      // This clears any manual override from the toggle
      setTheme("system");
    };

    // Modern browsers
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
    } else if (mq.addListener) {
      // Safari/older
      mq.addListener(onChange);
    }

    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", onChange);
      } else if (mq.removeListener) {
        mq.removeListener(onChange);
      }
    };
  }, []);

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === "system") {
        // If currently on system, toggle to opposite of current system theme
        return systemDark ? "light" : "dark";
      }
      // If manually set, just toggle between dark and light
      return prev === "dark" ? "light" : "dark";
    });
  };

  const value = useMemo(() => ({ theme, isDark, setTheme, toggleTheme }), [theme, isDark, systemDark]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);


